/**
 * Guía animada de instalación cargada en diferido (`React.lazy`): el guion, el mockup y su CSS
 * viven en un chunk aparte que solo se descarga cuando la guía se muestra, así el arranque de la
 * PWA no crece. Mientras llega, un marcador del mismo tamaño evita saltos de layout.
 */
import { lazy, Suspense, useEffect, useState } from 'react';

import { t } from '../../i18n';

import { FRAME, mockupScale, sizeOf, type DeviceInfo } from './variants';

import type { InstallGuideProps } from './install-guide';

const InstallGuide = lazy(() => import('./install-guide'));

function viewport(): { width: number; height: number } {
  return { width: window.innerWidth, height: window.innerHeight };
}

/** Alto/ancho de la ventana, reactivo a `resize` (rotación, teclado). */
function useViewport(): { width: number; height: number } {
  const [size, setSize] = useState(viewport);
  useEffect(() => {
    const onResize = () => setSize(viewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

export interface InstallGuideLazyProps extends Omit<InstallGuideProps, 'scale' | 'info'> {
  info?: Pick<DeviceInfo, 'iosMajor' | 'browser'>;
  /** Alto reservado para el resto de la pantalla al calcular la escala (brief §5). */
  reserve?: number;
}

export function InstallGuideLazy({ reserve, ...props }: InstallGuideLazyProps) {
  const { width, height } = useViewport();
  const scale = mockupScale(props.variantId, height, width, reserve);
  const { W, H } = sizeOf(props.variantId);
  const fallback = (
    <div
      data-testid="install-guide-loading"
      role="status"
      className="mx-auto flex items-center justify-center rounded-fm-xl bg-fm-bg-sunken text-fm-sm text-fm-text-secondary"
      style={{ width: (W + FRAME) * scale, height: (H + FRAME) * scale }}
    >
      {t('install.guide.loading')}
    </div>
  );
  return (
    <Suspense fallback={fallback}>
      <InstallGuide key={props.variantId} {...props} scale={scale} />
    </Suspense>
  );
}
